import React, { useState } from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, AlertTriangle, CheckCircle2 } from 'lucide-react';
export function TreatmentBehavior({
  state,
  updateState,
  nextStep
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const color = SPECIALTY_COLORS[role];
  const [showFeedback, setShowFeedback] = useState(!!state.predictedBehavior);
  const options = {
    Nephrologist: [{
      id: 'Early',
      text: 'They initiate multi-modal treatment early and aggressively',
      isCorrect: false
    }, {
      id: 'Mid',
      text: 'They focus on foundational therapies and escalate cautiously',
      isCorrect: false
    }, {
      id: 'Late',
      text: 'They reserve new treatments for late-stage progression',
      isCorrect: true
    }],
    Cardiologist: [{
      id: 'Early',
      text: 'They initiate multi-modal treatment early and aggressively',
      isCorrect: true
    }, {
      id: 'Mid',
      text: 'They focus on foundational therapies and escalate cautiously',
      isCorrect: false
    }, {
      id: 'Late',
      text: 'They reserve new treatments for late-stage progression',
      isCorrect: false
    }],
    Endocrinologist: [{
      id: 'Early',
      text: 'They initiate multi-modal treatment early and aggressively',
      isCorrect: false
    }, {
      id: 'Mid',
      text: 'They escalate metabolically but refer out for non-glycaemic treatment',
      isCorrect: true
    }, {
      id: 'Late',
      text: 'They reserve new treatments for late-stage progression',
      isCorrect: false
    }],
    PCP: [{
      id: 'Early',
      text: 'They initiate multi-modal treatment early and aggressively',
      isCorrect: false
    }, {
      id: 'Mid',
      text: 'They focus on foundational therapies and escalate cautiously',
      isCorrect: true
    }, {
      id: 'Late',
      text: 'They reserve new treatments for late-stage progression',
      isCorrect: false
    }]
  };
  const currentOptions = options[role];
  const handleSelect = (id: string) => {
    if (showFeedback) return;
    updateState({
      predictedBehavior: id
    });
    setTimeout(() => setShowFeedback(true), 500);
  };
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto" data-id="element-517">
      <div className="max-w-3xl w-full mx-auto" data-id="element-518">
        <div className="text-center mb-10" data-id="element-519">
          <span className="text-sm font-bold uppercase tracking-widest mb-2 block" style={{
          color
        }} data-id="element-520">
            Behavior Prediction
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-dark" data-id="element-521">
            How do {role}s typically approach treatment?
          </h2>
          <p className="text-gray-500 mt-2" data-id="element-522">
            Select the statement that best describes their reality.
          </p>
        </div>

        <div className="space-y-4 mb-10" data-id="element-523">
          {currentOptions.map(opt => {
          const isSelected = state.predictedBehavior === opt.id;
          const isFaded = showFeedback && !opt.isCorrect && !isSelected;
          const showCorrect = showFeedback && opt.isCorrect;
          const showIncorrect = showFeedback && isSelected && !opt.isCorrect;
          return <motion.button key={opt.id} whileHover={!showFeedback ? {
            scale: 1.01
          } : {}} whileTap={!showFeedback ? {
            scale: 0.99
          } : {}} onClick={() => handleSelect(opt.id)} className={`
                  w-full p-6 rounded-2xl border-2 text-left flex items-center justify-between transition-all duration-300
                  ${isSelected && !showFeedback ? 'bg-gray-50' : 'bg-white'}
                  ${showCorrect ? 'bg-green-50 border-green-500' : ''}
                  ${showIncorrect ? 'bg-red-50 border-red-300' : ''}
                  ${!showCorrect && !showIncorrect && !isSelected ? 'border-gray-200 hover:border-gray-300' : ''}
                  ${isFaded ? 'opacity-40 grayscale' : 'opacity-100'}
                `} style={{
            borderColor: isSelected && !showFeedback ? color : undefined
          }} data-id="element-524">
                <span className={`text-lg font-medium ${showCorrect ? 'text-green-800' : showIncorrect ? 'text-red-800' : 'text-dark'}`} data-id="element-525">
                  {opt.text}
                </span>

                {showCorrect && <CheckCircle2 className="text-green-500 flex-shrink-0 ml-4" size={24} data-id="element-526" />}

                {!showFeedback && <div className={`
                    w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center ml-4
                    ${isSelected ? 'border-transparent' : 'border-gray-300'}
                  `} style={{
              backgroundColor: isSelected ? color : 'transparent'
            }} data-id="element-527">
                    {isSelected && <div className="w-2 h-2 bg-white rounded-full" data-id="element-528" />}
                  </div>}
              </motion.button>;
        })}
        </div>

        <AnimatePresence data-id="element-529">
          {showFeedback && <motion.div initial={{
          opacity: 0,
          height: 0,
          y: 20
        }} animate={{
          opacity: 1,
          height: 'auto',
          y: 0
        }} className="overflow-hidden" data-id="element-530">
              <div className="bg-gray-50 border border-gray-200 rounded-3xl p-8 mb-8" data-id="element-531">
                <h3 className="text-xl font-bold mb-3" style={{
              color
            }} data-id="element-532">
                  Timing Insight
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6" data-id="element-533">
                  {role === 'Nephrologist' && 'Nephrologists often receive patients late in the disease progression. Combined with safety concerns, this leads to a more reactive, cautious approach rather than early multi-modal initiation.'}
                  {role === 'Cardiologist' && 'Cardiologists are highly outcome-driven and comfortable with rapid escalation. They are more likely to initiate multi-modal therapies early to prevent downstream events.'}
                  {role === 'Endocrinologist' && 'Endocrinologists are proactive with metabolic control (SGLT2/GLP1) but often hesitate to escalate to non-glycaemic treatments, preferring to refer out.'}
                  {role === 'PCP' && 'PCPs manage early-stage patients but lack confidence in complex escalation, preferring to stick with foundational therapies and refer when progression occurs.'}
                </p>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-3xl p-8 mb-8 relative overflow-hidden" data-id="element-534">
                <div className="absolute top-0 right-0 p-8 opacity-10 text-red-500 pointer-events-none" data-id="element-535">
                  <AlertTriangle size={120} data-id="element-536" />
                </div>

                <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center" data-id="element-537">
                  <AlertTriangle className="mr-2" size={24} data-id="element-538" />
                  The Cost of Delayed Treatment
                </h3>

                <ul className="space-y-3 relative z-10" data-id="element-539">
                  <li className="flex items-start text-red-900" data-id="element-540">
                    <span className="mr-3 mt-1.5 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" data-id="element-541"></span>
                    <span data-id="element-542">
                      <strong data-id="element-543">Faster CKD progression:</strong> Irreversible loss
                      of kidney function.
                    </span>
                  </li>
                  <li className="flex items-start text-red-900" data-id="element-544">
                    <span className="mr-3 mt-1.5 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" data-id="element-545"></span>
                    <span data-id="element-546">
                      <strong data-id="element-547">Increased CV risk:</strong> Patients face up to 3x
                      higher cardiovascular risk.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="flex justify-center pb-8" data-id="element-548">
                <button onClick={nextStep} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-549">
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-550" />
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}